// Gemeinsamer Veraltet-Hinweis und Aktualisieren-Button für einen Auftrag
// (docs/architecture/color-catalog-revisions.md, F7). Farben sind der erste
// Beitrag; weitere Beiträge (z. B. Assetrevisionen aus U08c.3) melden sich über
// registerOrderUpdateProvider an. Der Dienst kennt keine Produkte und keine
// Katalogdetails: Er sammelt Vorschaueinträge, zählt sie für den Hinweis und
// führt die Übernahme aller Beiträge in genau einem Undo-Schritt aus.
//
// Vertrag eines Beitrags:
//   {
//     id: 'colors',                  // eindeutig
//     label: 'Farben',               // Überschrift im Dialog
//     count(context) -> number,      // synchron, nur geladene Daten, für den Hinweis
//     collect(context) -> Promise<items[]>,   // vollständige Vorschau (darf nachladen)
//     apply(items, context) -> boolean        // synchron, ändert nur S.project-Daten
//   }
// items: { key, label, before: { label, swatchStyle? }, after: { label, swatchStyle? },
//          details?: string[] } – nur Anzeige, der Beitrag deutet sie selbst.

const providers = new Map();
const listeners = new Set();
let pendingTimer = null;
let lastSignature = '';
let lastCount = 0;
let signatureSource = () => '';
let contextSource = () => ({});

export function registerOrderUpdateProvider(provider) {
  if (!provider || typeof provider.id !== 'string' || !provider.id || providers.has(provider.id)) {
    throw new Error('Ungültiger oder doppelter Aktualisierungsbeitrag.');
  }
  for (const method of ['count', 'collect', 'apply']) {
    if (typeof provider[method] !== 'function') throw new Error(`Aktualisierungsbeitrag ohne ${method}().`);
  }
  providers.set(provider.id, Object.freeze({ ...provider, label: String(provider.label || provider.id) }));
  scheduleOrderUpdateCheck({ force: true });
  return () => {
    providers.delete(provider.id);
    scheduleOrderUpdateCheck({ force: true });
  };
}

export function listOrderUpdateProviders() {
  return [...providers.values()];
}

// Der Host liefert eine Signatur des Auftragsstands (Projekt, Ebenen, Revisionen),
// damit der Hinweis nur bei Änderungen neu gezählt wird, und den Lesekontext.
export function configureOrderUpdateHost({ signature, context } = {}) {
  if (typeof signature === 'function') signatureSource = signature;
  if (typeof context === 'function') contextSource = context;
}

export function onOrderUpdateCount(listener) {
  if (typeof listener !== 'function') return () => false;
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function safeCount(provider, context) {
  try {
    const value = Number(provider.count(context));
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  } catch (error) {
    console.warn(`Aktualisierungsbeitrag ${provider.id} konnte nicht gezählt werden.`, error);
    return 0;
  }
}

export function countOrderUpdates(context = contextSource()) {
  let total = 0;
  for (const provider of providers.values()) total += safeCount(provider, context);
  return total;
}

function notify(count) {
  for (const listener of listeners) {
    try { listener(count); } catch (error) { console.warn('Aktualisierungshinweis fehlgeschlagen.', error); }
  }
}

// Gebündelt und zeitversetzt nach Refreshes: das Durchsuchen eines großen
// Auftrags gehört nicht in jeden Frame.
export function scheduleOrderUpdateCheck({ force = false, delayMs = 400 } = {}) {
  if (force) lastSignature = '';
  if (pendingTimer !== null) return;
  pendingTimer = setTimeout(() => {
    pendingTimer = null;
    let signature;
    try { signature = String(signatureSource() || ''); } catch (_error) { signature = ''; }
    if (signature && signature === lastSignature) return;
    lastSignature = signature;
    lastCount = countOrderUpdates();
    notify(lastCount);
  }, delayMs);
}

export function currentOrderUpdateCount() {
  return lastCount;
}

// Vollständige Vorschau aller Beiträge; fehlerhafte Beiträge erscheinen mit
// Fehlermeldung statt die anderen zu verhindern.
export async function collectOrderUpdates(context = contextSource()) {
  const groups = [];
  for (const provider of providers.values()) {
    try {
      const items = await provider.collect(context);
      groups.push({ id: provider.id, label: provider.label, items: Array.isArray(items) ? items : [], error: '' });
    } catch (error) {
      groups.push({ id: provider.id, label: provider.label, items: [], error: String(error?.message || error) });
    }
  }
  return groups;
}

// Übernimmt die gewählten Gruppen. `recordUndo` wird genau einmal vor der ersten
// Änderung aufgerufen; der Host übergibt ihn zusammen mit dem Abschluss-Refresh.
export function applyOrderUpdates(groups, { recordUndo, finish, label = 'Auftrag aktualisieren', context = contextSource() } = {}) {
  const selected = (Array.isArray(groups) ? groups : []).filter(group => group?.items?.length && providers.has(group.id));
  if (!selected.length) return { applied: [], changed: false };
  recordUndo?.(label);
  const applied = [];
  for (const group of selected) {
    if (providers.get(group.id).apply(group.items, context) === true) applied.push(group.id);
  }
  finish?.(applied);
  scheduleOrderUpdateCheck({ force: true, delayMs: 0 });
  return { applied, changed: applied.length > 0 };
}

// Nur für Tests.
export function resetOrderUpdateServiceForTests() {
  providers.clear();
  listeners.clear();
  if (pendingTimer !== null) clearTimeout(pendingTimer);
  pendingTimer = null;
  lastSignature = '';
  lastCount = 0;
  signatureSource = () => '';
  contextSource = () => ({});
}
